import React, { forwardRef, useContext, useEffect, useState } from 'react';
import { cx } from '@linaria/atomic';
import { MotionProps, PanInfo, motion, useAnimation } from 'motion/react';
import { useMediaMatches } from '@frontend/responsiveness';
import type { CommonHTMLAttributes } from '@frontend/types';
import { FontColorType } from '../../helpers';
import { ConfirmBadgeIcon, ErrorBadgeIcon, InfoBadgeIcon, WarningBadgeIcon, XIconSmall } from '../../icon';
import { Text } from '../text';
import { TextButton } from '../text-button';
import {
  alertActionContainerStyle,
  alertActionButtonStyle,
  alertContentBaseStyle,
  alertContentNewAlertStyle,
  alertContentNewAlertTypeStyles,
  alertIconWithActionStyle,
  alertNewAlertActionButtonStyle,
  alertWrapperStyle,
  closeButtonStyle,
  containerStyle,
  textBaseStyle,
  textBeforeTypeStyles,
  textNewAlertStyle,
  actionButtonStyles,
} from './alert.styles';
import { AlertsVariantContext } from './alerts.provider';
import { actionButtonVariants, alertTypeIconVariants, buttonAnimation, newButtonAnimation } from './constants';
import { AlertType, AlertAction } from './types';

type AlertProps = Omit<CommonHTMLAttributes, 'onClick'> & {
  children: React.ReactNode;
  id?: string;
  onClick?: (id: string) => void;
  type: AlertType;
  action?: AlertAction;
  index?: number;
} & Omit<MotionProps, 'onClick'>;

const iconMap = {
  error: ErrorBadgeIcon,
  info: InfoBadgeIcon,
  success: ConfirmBadgeIcon,
  warning: WarningBadgeIcon,
};

const iconColorMap: { [key: string]: FontColorType } = {
  error: 'error',
  info: 'primary',
  success: 'success',
  warning: 'warn',
};

const toastVariants = {
  hidden: { opacity: 0, y: 50, scale: 0.9 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.4, ease: 'easeOut', staggerChildren: 0.4 } },
  exit: { opacity: 0, y: -50, scale: 0.9, zIndex: -1, transition: { duration: 0.3, ease: 'easeIn' } },
  slideOut: { x: 400, opacity: 0, transition: { duration: 0.1 } },
};

export const Alert = forwardRef<HTMLParagraphElement, AlertProps>(
  ({ children, id, onClick, type, action, index, ...rest }: AlertProps, ref) => {
    const controls = useAnimation();
    const { matches } = useMediaMatches();
    const isMobileScreen = matches.useXSmallMax();
    const [slideOut, setSlideOut] = useState(false);
    const newAlert = useContext(AlertsVariantContext) === 'experimental';
    const [hovered, setHovered] = useState(false);
    const Icon = iconMap[type];

    let handleClick;
    if (typeof onClick === 'function') {
      handleClick = () => onClick(id ?? '');
    }

    useEffect(() => {
      if (handleClick) {
        controls.start('show');
      }
    }, []);

    const handleDragEnd = (_, { offset }: PanInfo) => {
      if (offset.x > 200) {
        setSlideOut(true);
        controls.start('slideOut').then(handleClick);
      } else {
        controls.start({ x: 0, transition: { duration: 0.2 } }); // Snap back if not far enough
      }
    };

    const handleDrag = (_, { offset }: PanInfo) => {
      if (offset.x < 0) {
        controls.start({ x: 0 });
      }
    };

    const buttonProps = newAlert
      ? {
          initial: 'hidden',
          animate: hovered ? 'hovering' : 'hidden',
          variants: newButtonAnimation,
        }
      : {
          initial: 'hidden',
          animate: 'show',
          variants: buttonAnimation,
        };

    return (
      <motion.div
        className={alertWrapperStyle}
        style={index ? { zIndex: index } : undefined}
        {...rest}
        drag={isMobileScreen && !!handleClick ? 'x' : undefined}
        dragConstraints={{ left: 0, right: 0 }}
        dragElastic={0.5}
        onDrag={handleDrag}
        onDragEnd={handleDragEnd}
        animate={!!handleClick ? controls : undefined}
        initial={!!handleClick ? 'hidden' : undefined}
        onHoverStart={() => setHovered(true)}
        onHoverEnd={() => setHovered(false)}
        exit={!!handleClick ? (slideOut ? {} : 'exit') : undefined}
        layout={!!handleClick ? 'position' : false}
        variants={!!handleClick ? toastVariants : undefined}
        ref={ref}
      >
        {!!handleClick && <XButton onClose={handleClick} {...buttonProps} />}
        {newAlert && (
          <motion.div
            className={closeButtonStyle}
            variants={alertTypeIconVariants}
            initial='initial'
            animate={hovered ? 'hovering' : 'initial'}
            exit={'exit'}
          >
            <Icon color={iconColorMap[type]} />
          </motion.div>
        )}
        <motion.div
          className={containerStyle}
          aria-live={type === 'error' || type === 'warning' ? 'assertive' : 'polite'}
        >
          <motion.div
            className={cx(
              alertContentBaseStyle,
              newAlert && alertContentNewAlertStyle,
              newAlert && alertContentNewAlertTypeStyles[type]
            )}
          >
            {!newAlert && <Icon className={action ? alertIconWithActionStyle : undefined} color={iconColorMap[type]} />}
            <Text
              size='medium'
              className={cx(textBaseStyle, textBeforeTypeStyles[type], newAlert && textNewAlertStyle)}
            >
              {children}
            </Text>
          </motion.div>
          {action && !newAlert && (
            <motion.div className={alertActionContainerStyle}>
              <TextButton
                onClick={(e) => {
                  action.onClick(e);
                  handleClick();
                }}
                className={alertActionButtonStyle}
                trackingId={action.trackingId}
              >
                {action.label}
              </TextButton>
            </motion.div>
          )}
        </motion.div>
        {action && newAlert && (
          <motion.div
            className={actionButtonStyles}
            initial='initial'
            exit='exit'
            animate={hovered ? 'hovering' : 'initial'}
            variants={actionButtonVariants}
          >
            <TextButton
              className={alertNewAlertActionButtonStyle}
              trackingId={action.trackingId}
              onClick={(e) => {
                action.onClick(e);
                handleClick();
              }}
            >
              {action.label}
            </TextButton>
          </motion.div>
        )}
      </motion.div>
    );
  }
);
Alert.displayName = 'Alert';

type XButtonProps = {
  onClose: () => void;
} & MotionProps;

export const XButton = ({ onClose, ...rest }: XButtonProps) => {
  return (
    <motion.button
      {...rest}
      exit='exit'
      onClick={(e) => {
        e.stopPropagation();
        onClose();
      }}
      className={closeButtonStyle}
    >
      <XIconSmall />
    </motion.button>
  );
};
